/**
 * IntegrationsAdmin — FM7 Integration Configuration Workspace
 * Route: /admin/integrations
 * Authorization: FM7_IMPLEMENTATION_PLAN.md (BOUND-REOPEN-FM7-01)
 *
 * RBAC:
 *   finance.integrations.view      → read-only access (all admin roles)
 *   finance.integrations.configure → create, update, enable, disable, delete
 *   finance.integrations.execute   → trigger test-connection
 *
 * This is the ONLY place where integrations are configured.
 * Finance surfaces CONSUME capability state — they do not configure.
 *
 * STATUS SEMANTICS (INV-FM7-FOUND-01):
 *   NOT_CONFIGURED → no provider registered → capability unavailable
 *   CONFIGURED     → registered, not tested → not operational yet
 *   VALIDATING     → test in progress → unavailable
 *   ACTIVE         → tested + enabled → capability available
 *   DEGRADED       → was active, test now failing → degraded
 *   ERROR          → explicit error → unavailable
 *   DISABLED       → admin-disabled → unavailable
 *
 * INVARIANT: CONFIGURED ≠ ACTIVE.
 *   Finance-surface action buttons light up ONLY on ACTIVE.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Zap, ZapOff, Trash2, TestTube2, ChevronDown, ChevronRight,
  CreditCard, Database, Bell, Brain, Building2, Settings,
  Clock, ShieldCheck, Info, Activity,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { PageHeader } from "../components/layout/Shell";
import { Badge } from "../components/ui/badge";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ── Role sets ─────────────────────────────────────────────────────────────────
const VIEW_ROLES      = new Set(["super_admin", "institution_admin"]);
const CONFIGURE_ROLES = new Set(["super_admin", "institution_admin"]);
const EXECUTE_ROLES   = new Set(["super_admin", "institution_admin"]);

// ── Category metadata ─────────────────────────────────────────────────────────
const CATEGORIES = [
  {
    id: "PAYMENTS",
    label: "Payment Gateway",
    icon: CreditCard,
    description:
      "Payment gateway for collecting student fees online. ACTIVE providers unlock 'Pay Now' on student finance screens.",
    actionNote: "Enables: Pay Now on My Finance",
    color: "blue",
  },
  {
    id: "ERP",
    label: "ERP / Accounting",
    icon: Database,
    description:
      "ERP and accounting integration for exporting financial records. ACTIVE providers unlock 'Export to ERP' on Finance Reports.",
    actionNote: "Enables: Export to ERP on Finance Reports",
    color: "violet",
  },
  {
    id: "NOTIFICATIONS",
    label: "Notifications",
    icon: Bell,
    description:
      "Email, SMS, or in-app notification service. ACTIVE providers unlock 'Send Reminder' on Collections workspace.",
    actionNote: "Enables: Send Reminder on Collections",
    color: "amber",
  },
  {
    id: "AI_PROVIDERS",
    label: "AI Providers",
    icon: Brain,
    description:
      "AI advisory integration for finance explanation, draft letters, and document extraction. Output is advisory only — not a canonical financial authority.",
    actionNote: "Enables: Explain with AI on Finance views",
    color: "emerald",
  },
  {
    id: "GOVERNMENT",
    label: "Government Schemes",
    icon: Building2,
    description:
      "Government scholarship and loan portal connectivity. ACTIVE enables automated government disbursement status.",
    actionNote: "Enables: Govt scheme sync",
    color: "slate",
  },
];

// ── Status badge config ────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  NOT_CONFIGURED: { label: "NOT CONFIGURED", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  CONFIGURED:     { label: "CONFIGURED",     cls: "bg-blue-50 text-blue-700 border-blue-200"    },
  VALIDATING:     { label: "VALIDATING",     cls: "bg-amber-50 text-amber-700 border-amber-200" },
  ACTIVE:         { label: "ACTIVE",         cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  DEGRADED:       { label: "DEGRADED",       cls: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500" },
  ERROR:          { label: "ERROR",          cls: "bg-red-50 text-red-700 border-red-200"        },
  DISABLED:       { label: "DISABLED",       cls: "bg-slate-100 text-slate-400 border-slate-200" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.NOT_CONFIGURED;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold border rounded ${cfg.cls}`}
      data-testid={`status-badge-${status?.toLowerCase()}`}
    >
      {cfg.dot && <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />}
      {cfg.label}
    </span>
  );
}

// ── Invariant notice (CONFIGURED ≠ ACTIVE) ───────────────────────────────────
function ConfiguredWarning() {
  return (
    <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid="configured-not-active-notice">
      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <div>
        <strong>CONFIGURED ≠ ACTIVE (INV-FM7-FOUND-01)</strong>
        <span className="ml-1">This provider is registered but not yet operational. Run "Test Connection", then enable it to reach ACTIVE status.</span>
      </div>
    </div>
  );
}

// ── Register Integration Modal ─────────────────────────────────────────────────
function RegisterModal({ categoryId, catalogue, onClose, onDone }) {
  const providers = catalogue[categoryId] || [];
  const [form, setForm] = useState({
    provider: providers[0] || "",
    display_name: "",
    config_json: "{}",
    secret_reference: "",
  });
  const [saving, setSaving] = useState(false);

  const register = async () => {
    if (!form.provider) { toast.error("Select a provider"); return; }
    let cfg = {};
    try { cfg = JSON.parse(form.config_json || "{}"); }
    catch { toast.error("Config JSON is invalid"); return; }

    setSaving(true);
    try {
      await api.post("/finance/integrations", {
        category: categoryId,
        provider: form.provider,
        display_name: form.display_name || form.provider,
        config_json: cfg,
        secret_reference: form.secret_reference || undefined,
      });
      toast.success("Integration registered. Run Test Connection to activate.");
      onDone();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Registration failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="register-integration-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Register Integration — {categoryId}
          </h3>
          <button onClick={onClose}><XCircle className="w-4 h-4 text-slate-400" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />
            <strong>Real external providers are NOT yet connected.</strong> Only test adapters
            (prefixed <code>test_</code>) are operational in this environment.
            Production providers (Stripe, SAP, etc.) require credentials — configure those when ready.
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Provider *</label>
            <select
              data-testid="register-provider-select"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.provider}
              onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}
            >
              {providers.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Display Name</label>
            <input
              data-testid="register-display-name"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              placeholder={form.provider}
              value={form.display_name}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Config JSON <span className="text-slate-400 font-normal">(provider-specific params)</span>
            </label>
            <textarea
              data-testid="register-config-json"
              rows={3}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"
              placeholder="{}"
              value={form.config_json}
              onChange={e => setForm(f => ({ ...f, config_json: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Secret Reference <span className="text-slate-400 font-normal">(vault path or env key name — no raw secrets)</span>
            </label>
            <input
              data-testid="register-secret-reference"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              placeholder="e.g. vault:finance/payment/api_key"
              value={form.secret_reference}
              onChange={e => setForm(f => ({ ...f, secret_reference: e.target.value }))}
            />
          </div>
        </div>

        <div className="px-5 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">
            Cancel
          </button>
          <button
            data-testid="register-submit-btn"
            onClick={register}
            disabled={saving}
            className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Registering…" : "Register"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Integration Card ───────────────────────────────────────────────────────────
function IntegrationCard({ integration, canConfigure, canExecute, onRefresh }) {
  const [expanded, setExpanded]   = useState(false);
  const [busy, setBusy]           = useState(null);   // 'test' | 'enable' | 'disable' | 'delete'

  const act = async (action, label) => {
    setBusy(action);
    try {
      if (action === "test") {
        const resp = await api.post(`/finance/integrations/${integration.id}/test`);
        const ok = resp.data?.test_result?.success;
        toast[ok ? "success" : "error"](
          ok ? `Test passed — ${integration.display_name}` : `Test failed — ${resp.data?.test_result?.error || "check provider config"}`
        );
      } else if (action === "enable") {
        await api.post(`/finance/integrations/${integration.id}/enable`);
        toast.success(`${integration.display_name} enabled`);
      } else if (action === "disable") {
        await api.post(`/finance/integrations/${integration.id}/disable`);
        toast.warning(`${integration.display_name} disabled`);
      } else if (action === "delete") {
        if (!window.confirm(`Delete ${integration.display_name}? This cannot be undone.`)) { setBusy(null); return; }
        await api.delete(`/finance/integrations/${integration.id}`);
        toast.success("Integration deleted");
      }
      onRefresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : `${label} failed`);
    } finally { setBusy(null); }
  };

  const fmtDate = (v) => v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
  const isBusy  = (a) => busy === a;

  return (
    <div
      className="border border-slate-200 rounded-xl bg-white overflow-hidden"
      data-testid={`integration-card-${integration.id}`}
    >
      {/* Summary row */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left"
        data-testid={`integration-expand-${integration.id}`}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge status={integration.status} />
          <span className="font-medium text-sm text-slate-800">{integration.display_name}</span>
          <span className="text-xs text-slate-400 font-mono">{integration.provider}</span>
          {integration.is_active && (
            <span className="text-xs text-emerald-600 font-medium">● active</span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 space-y-3">
          {/* Invariant notice for CONFIGURED state */}
          {integration.status === "CONFIGURED" && <ConfiguredWarning />}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
            <div>
              <span className="text-slate-400">Last validated:</span>{" "}
              <span>{fmtDate(integration.last_validated_at)}</span>
            </div>
            <div>
              <span className="text-slate-400">Last success:</span>{" "}
              <span>{fmtDate(integration.last_success_at)}</span>
            </div>
            {integration.last_error && (
              <div className="col-span-2 flex items-start gap-1.5 p-2 bg-red-50 border border-red-200 rounded text-red-700">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span><strong>Last error:</strong> {integration.last_error}</span>
              </div>
            )}
            <div>
              <span className="text-slate-400">Secret ref:</span>{" "}
              <span className="font-mono">
                {integration.secret_reference_present ? "● present" : "not set"}
              </span>
            </div>
          </div>

          {/* Action buttons */}
          {(canExecute || canConfigure) && (
            <div className="flex flex-wrap gap-2 pt-1">
              {canExecute && (
                <button
                  data-testid={`test-connection-btn-${integration.id}`}
                  onClick={() => act("test", "Test")}
                  disabled={!!busy}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
                >
                  <TestTube2 className="w-3.5 h-3.5" />
                  {isBusy("test") ? "Testing…" : "Test Connection"}
                </button>
              )}
              {canConfigure && !integration.is_active && integration.status === "ACTIVE" && (
                <button
                  data-testid={`enable-btn-${integration.id}`}
                  onClick={() => act("enable", "Enable")}
                  disabled={!!busy}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  <Zap className="w-3.5 h-3.5" />
                  {isBusy("enable") ? "Enabling…" : "Enable"}
                </button>
              )}
              {canConfigure && integration.is_active && (
                <button
                  data-testid={`disable-btn-${integration.id}`}
                  onClick={() => act("disable", "Disable")}
                  disabled={!!busy}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-50"
                >
                  <ZapOff className="w-3.5 h-3.5" />
                  {isBusy("disable") ? "Disabling…" : "Disable"}
                </button>
              )}
              {canConfigure && (
                <button
                  data-testid={`delete-integration-btn-${integration.id}`}
                  onClick={() => act("delete", "Delete")}
                  disabled={!!busy}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 ml-auto"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {isBusy("delete") ? "Deleting…" : "Delete"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Execution History Panel — BOUND-REOPEN-FM8-03 ────────────────────────────
// Surfaces existing FM7B/C/D log APIs with no new integration behavior.
// Invariant: read-only display only. INV-FM8-REOPEN-01 satisfied.
function ExecutionHistoryPanel({ categoryId, canView }) {
  const [records, setRecords]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    if (!expanded) { setExpanded(true); setLoading(true); }
    try {
      let url = "";
      if (categoryId === "PAYMENTS")       url = "/finance/payment-gateway/intents";
      else if (categoryId === "ERP")       url = "/finance/erp/exports";
      else if (categoryId === "NOTIFICATIONS") url = "/finance/notifications";
      else if (categoryId === "AI_PROVIDERS")  url = "/finance/ai/history";
      else { setRecords([]); setLoading(false); return; }

      const resp = await api.get(url);
      // Normalise different list-field names per endpoint
      const list =
        resp.data?.intents ||
        resp.data?.exports ||
        resp.data?.deliveries ||
        resp.data?.ai_events ||
        [];
      setRecords(list);
    } catch { setRecords([]); }
    finally { setLoading(false); }
  };

  const toggle = () => {
    if (!expanded) load();
    else setExpanded(false);
  };

  const fmtDate = (v) =>
    v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  const FIELD_MAPS = {
    PAYMENTS:      [["id", "Intent ID"], ["provider", "Provider"], ["amount", "Amount"], ["status", "Status"], ["created_at", "Created"]],
    ERP:           [["id", "Exec ID"], ["dataset", "Dataset"], ["provider", "Provider"], ["status", "Status"], ["record_count", "Records"], ["created_at", "Created"]],
    NOTIFICATIONS: [["id", "Delivery ID"], ["event_type", "Event"], ["channel", "Channel"], ["status", "Status"], ["created_at", "Created"]],
    AI_PROVIDERS:  [["id", "Event ID"], ["ai_function", "Function"], ["provider", "Provider"], ["status", "Status"], ["created_at", "Created"]],
  };
  const fields = FIELD_MAPS[categoryId] || [];

  if (!["PAYMENTS","ERP","NOTIFICATIONS","AI_PROVIDERS"].includes(categoryId)) return null;

  return (
    <div className="border-t border-slate-100" data-testid={`execution-history-${categoryId.toLowerCase()}`}>
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 text-left"
      >
        <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
          <Activity className="w-3.5 h-3.5" />
          Execution History
        </span>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-3">
          {loading ? (
            <div className="text-xs text-slate-400 py-3">Loading…</div>
          ) : records.length === 0 ? (
            <div className="text-xs text-slate-400 py-3 text-center">No execution records yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100">
                    {fields.map(([, label]) => (
                      <th key={label} className="text-left py-1.5 pr-3 text-slate-400 font-medium">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.slice(0, 10).map((r, i) => (
                    <tr key={r.id || i} className="border-b border-slate-50">
                      {fields.map(([key]) => (
                        <td key={key} className="py-1.5 pr-3 text-slate-600 font-mono">
                          {key === "created_at" ? fmtDate(r[key]) : String(r[key] ?? "—")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {records.length > 10 && (
                <p className="text-xs text-slate-400 mt-1.5">{records.length - 10} more records not shown.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
function CategoryPanel({ cat, integrations, catalogue, canConfigure, canExecute, canView, onRefresh }) {
  const [showRegister, setShowRegister] = useState(false);
  const Icon = cat.icon;
  const catIntegrations = integrations.filter(i => i.category === cat.id);
  const activeOne = catIntegrations.find(i => i.status === "ACTIVE");
  const colorMap = {
    blue:   "border-blue-200 bg-blue-50",
    violet: "border-violet-200 bg-violet-50",
    amber:  "border-amber-200 bg-amber-50",
    emerald:"border-emerald-200 bg-emerald-50",
    slate:  "border-slate-200 bg-slate-50",
  };
  const iconColorMap = {
    blue:   "text-blue-600",
    violet: "text-violet-600",
    amber:  "text-amber-600",
    emerald:"text-emerald-600",
    slate:  "text-slate-600",
  };

  return (
    <div className="border border-slate-200 rounded-xl bg-white" data-testid={`category-panel-${cat.id.toLowerCase()}`}>
      {/* Category header */}
      <div className={`flex items-start justify-between px-5 py-4 border-b border-slate-100 rounded-t-xl ${colorMap[cat.color]}`}>
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-white border border-slate-200`}>
            <Icon className={`w-4 h-4 ${iconColorMap[cat.color]}`} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 text-sm">{cat.label}</h3>
            <p className="text-xs text-slate-500 mt-0.5 max-w-lg">{cat.description}</p>
            <p className="text-xs text-slate-400 mt-1 italic">{cat.actionNote}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {activeOne ? (
            <StatusBadge status="ACTIVE" />
          ) : catIntegrations.length > 0 ? (
            <StatusBadge status={catIntegrations[0].status} />
          ) : (
            <StatusBadge status="NOT_CONFIGURED" />
          )}
          {canConfigure && (
            <button
              data-testid={`register-btn-${cat.id.toLowerCase()}`}
              onClick={() => setShowRegister(true)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-slate-800 text-white rounded-lg hover:bg-slate-700"
            >
              <Plus className="w-3 h-3" /> Add
            </button>
          )}
        </div>
      </div>

      {/* Integration cards */}
      <div className="p-4 space-y-2">
        {catIntegrations.length === 0 ? (
          <div className="flex items-center gap-3 py-6 justify-center text-sm text-slate-400" data-testid={`not-configured-${cat.id.toLowerCase()}`}>
            <Settings className="w-4 h-4 opacity-50" />
            <span>No integrations configured for this category.</span>
            {canConfigure && (
              <button
                onClick={() => setShowRegister(true)}
                className="ml-2 text-blue-600 hover:underline text-xs font-medium"
              >
                Register one
              </button>
            )}
          </div>
        ) : (
          catIntegrations.map(i => (
            <IntegrationCard
              key={i.id}
              integration={i}
              canConfigure={canConfigure}
              canExecute={canExecute}
              onRefresh={onRefresh}
            />
          ))
        )}
      </div>

      {showRegister && (
        <RegisterModal
          categoryId={cat.id}
          catalogue={catalogue}
          onClose={() => setShowRegister(false)}
          onDone={onRefresh}
        />
      )}

      {/* BOUND-REOPEN-FM8-03: Execution History — surfaces existing FM7 log APIs */}
      <ExecutionHistoryPanel categoryId={cat.id} canView={canView || canExecute} />
    </div>
  );
}

// ── Status Summary Bar ─────────────────────────────────────────────────────────
function StatusSummaryBar({ integrations }) {
  const active  = integrations.filter(i => i.status === "ACTIVE").length;
  const config  = integrations.filter(i => i.status === "CONFIGURED").length;
  const degraded = integrations.filter(i => ["DEGRADED", "ERROR"].includes(i.status)).length;
  const total   = integrations.length;

  return (
    <div className="flex flex-wrap gap-4 p-4 bg-slate-50 border border-slate-200 rounded-xl" data-testid="status-summary-bar">
      <div className="flex items-center gap-1.5 text-sm">
        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        <span className="font-semibold text-slate-700">{active}</span>
        <span className="text-slate-500">Active</span>
      </div>
      <div className="flex items-center gap-1.5 text-sm">
        <Clock className="w-4 h-4 text-blue-400" />
        <span className="font-semibold text-slate-700">{config}</span>
        <span className="text-slate-500">Configured (not yet active)</span>
      </div>
      {degraded > 0 && (
        <div className="flex items-center gap-1.5 text-sm">
          <AlertTriangle className="w-4 h-4 text-orange-500" />
          <span className="font-semibold text-orange-700">{degraded}</span>
          <span className="text-slate-500">Degraded / Error</span>
        </div>
      )}
      <div className="flex items-center gap-1.5 text-sm text-slate-400 ml-auto">
        <span>{total} registered total</span>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function IntegrationsAdmin() {
  const { user } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [catalogue, setCatalogue]       = useState({});
  const [loading, setLoading]           = useState(true);

  const canView      = VIEW_ROLES.has(user?.role);
  const canConfigure = CONFIGURE_ROLES.has(user?.role);
  const canExecute   = EXECUTE_ROLES.has(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get("/finance/integrations");
      setIntegrations(resp.data?.integrations || []);
      setCatalogue(resp.data?.catalogue || {});
    } catch (e) {
      toast.error("Failed to load integrations");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!canView) return <Navigate to="/" replace />;

  return (
    <div className="p-6 space-y-6" data-testid="integrations-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <PageHeader
            title="Integrations"
            description="Configure, test, and manage all external service integrations. Finance surfaces consume capability state — they do not configure."
          />
        </div>
        <button
          data-testid="refresh-integrations-btn"
          onClick={load}
          disabled={loading}
          className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50"
        >
          <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Status truth invariant banner */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800" data-testid="status-invariant-banner">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          <strong>Status Truth (INV-FM7-FOUND-01):</strong>{" "}
          <span className="font-mono">CONFIGURED</span> ≠ <span className="font-mono">ACTIVE</span>.
          Finance-surface actions (Pay Now, Export to ERP, Send Reminder, Explain with AI) are enabled
          ONLY when the corresponding integration status is <span className="font-mono font-semibold">ACTIVE</span>.
          Register → Test Connection → Enable to activate.
        </div>
      </div>

      {/* Status summary */}
      {!loading && <StatusSummaryBar integrations={integrations} />}

      {/* Category panels */}
      {loading ? (
        <div className="text-center py-12 text-slate-400" data-testid="integrations-loading">Loading integrations…</div>
      ) : (
        <div className="space-y-4">
          {CATEGORIES.map(cat => (
            <CategoryPanel
              key={cat.id}
              cat={cat}
              integrations={integrations}
              catalogue={catalogue}
              canConfigure={canConfigure}
              canExecute={canExecute}
              canView={canView}
              onRefresh={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}
